import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { Image } from '@archipro-design/aria';
import { ModuleColumnEnum } from 'generated/graphql';
import type {
    ModuleDataColumn,
    PageBuilderModuleMetaProps,
} from '@modules/page-builder/types';
import { PARAGRAPH_PHOTO_WIDTH } from '@modules/page-builder/config/column-constants';
import DetailSingleImageShell from '../detail-single-image-shell/DetailSingleImageShell';
import SingleParagraphModule from '../single-paragraph-module/SingleParagraphModule';
import { cn } from '@rocco/utils/cn';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import ProductInfo from '@modules/page-builder/component/common/product-info/ProductInfo';

interface ImageParagraphModuleProps extends PageBuilderModuleMetaProps {
    columns: ModuleDataColumn[];
    /** Project detail revamp (BC-4725) — restyles the caption to the Figma text style. */
    isProjectRevamp?: boolean;
}

/**
 * A rendered Page Builder module for an image and paragraph.
 *
 * The editor version is at
 * {@link packages/web/app/modules/page-builder/component/edit/image-paragraph-module/ImageParagraphModule.tsx}
 */
const ImageParagraphModule = ({
    columns,
    meta,
    isProjectRevamp,
}: ImageParagraphModuleProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);

    const isProduct = columns.some(
        (column) => column.productId !== undefined && column.productId !== null,
    );

    const isImageFirst = columns[0]?.type === ModuleColumnEnum.ImageColumn;

    return (
        <div
            className={cn(
                'mx-auto grid w-full grid-cols-1 gap-x-6 gap-y-7 md:mx-12 md:max-w-full',
                !isProduct
                    ? isImageFirst
                        ? 'md:grid-cols-[65%_35%]'
                        : 'md:grid-cols-[35%_65%]'
                    : 'md:grid-cols-2',
                // Project detail revamp (BC-4725): drop the module's own 48px
                // side margin so it aligns to the section content edge like the
                // other modules — the gallery section already provides the 3rem
                // gutter, which the md:mx-12 was doubling.
                isProjectRevamp && 'md:mx-0',
            )}
        >
            {columns.map((column) => {
                if (column.type === ModuleColumnEnum.ImageColumn) {
                    if (!column.imageUrl && isMobile) {
                        return null;
                    }
                    const imageCol = column.imageUrl ? (
                        <div
                            key={column.id}
                            className={cn(
                                'flex flex-col',
                                column.productId
                                    ? 'items-end justify-end md:justify-end'
                                    : 'items-start justify-center md:justify-start',
                            )}
                        >
                            <DetailSingleImageShell
                                caption={column.caption}
                                dimension={column.dimension}
                                libraryImage={column?.libraryImage}
                                tags={column.tags}
                                uniqueApprovedTags={column.uniqueApprovedTags}
                                imageURL={column.imageUrl}
                                columnId={column.id}
                                taggingEnabled={!column.productId}
                                displayReseneTool={!column.productId && false}
                                isProduct={!!column.productId}
                                className={
                                    column.productId
                                        ? 'h-auto! self-start'
                                        : 'self-start'
                                }
                                moduleType={
                                    column.productId
                                        ? 'FeaturedProductModule'
                                        : 'ImageParagraphModule'
                                }
                                isProjectRevamp={isProjectRevamp}
                            >
                                <Image
                                    src={column.imageUrl}
                                    layout={'fill'}
                                    objectFit={'cover'}
                                    width={column.dimension.width}
                                    height={column.dimension.height}
                                    sizes={getImagesSizes(
                                        PARAGRAPH_PHOTO_WIDTH,
                                    )}
                                    alt={
                                        column.caption ||
                                        column?.libraryImage?.Title ||
                                        ''
                                    }
                                    variables={{
                                        mixBlendMode: column.productId
                                            ? 'multiply'
                                            : 'normal',
                                    }}
                                    className="overflow-hidden md:rounded-2"
                                />
                            </DetailSingleImageShell>
                        </div>
                    ) : (
                        <div key={column.id}></div>
                    );
                    return column.productId ? (
                        <div
                            key={column.id}
                            className={cn(
                                'flex scale-down-variant-caption flex-col items-start',
                                'justify-start md:items-end',
                            )}
                        >
                            <ProductInfo
                                isProjectRevamp={isProjectRevamp}
                                productId={column.productId}
                                dimension={column.dimension}
                                variant={'02'}
                                image={imageCol}
                                clickable={true}
                                moduleType="FeaturedProductModule"
                                className="max-w-full min-w-0 md:mr-0! md:ml-auto!"
                            />
                        </div>
                    ) : (
                        imageCol
                    );
                } else if (column.type === ModuleColumnEnum.TextColumn) {
                    if (!column.content && isMobile) {
                        return null;
                    }
                    return (
                        <SingleParagraphModule
                            isNewDesign={true}
                            key={column.id}
                            column={column}
                            meta={meta}
                            moduleType={
                                isProduct
                                    ? 'FeaturedProductModule'
                                    : 'ImageParagraphModule'
                            }
                            isProjectRevamp={isProjectRevamp}
                        />
                    );
                }
                return null;
            })}
        </div>
    );
};

export default ImageParagraphModule;
